import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

export interface ActionMenuItem {
  id: string;
  iconRef: string;
  label?: string;
  disabled: boolean;
  locked?: boolean;
}

@Component({
  selector: 'app-common-actions-menu',
  templateUrl: './common-actions-menu.component.html',
  styleUrls: ['./common-actions-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
})
export class CommonActionsMenuComponent {
  @Input() items: ActionMenuItem[] = [];
  @Input() iconSize = 16;
  @Input() totalCount = 0;

  @Output() actionClicked = new EventEmitter<string>();

  @ViewChild('ellipsisButton') ellipsisButton!: ElementRef<HTMLButtonElement>;

  isMenuOpen = false;
  isMenuEnabled = true;

  constructor(private elementRef: ElementRef) {}

  toggleMenu(event: Event): void {
    event.stopPropagation();

    if (!this.isMenuEnabled) {
      return;
    }

    this.isMenuOpen = !this.isMenuOpen;

    if (this.isMenuOpen) {
      this.isMenuEnabled = false;
    }
  }

  closeMenu(): void {
    if (this.isMenuOpen) {
      this.isMenuOpen = false;
      this.isMenuEnabled = true;
    }
  }

  onAction(item: ActionMenuItem, event: Event): void {
    event.stopPropagation();
    if (!item.disabled) {
      this.actionClicked.emit(item.id);
      this.closeMenu();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isMenuOpen || this.isMouseOverComponent(event)) {
      return;
    }

    this.closeMenu();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    if (!this.isMenuOpen) {
      return;
    }

    if (!this.isMouseOverComponent(event)) {
      this.closeMenu();
    }
  }

  private isMouseOverComponent(event: MouseEvent): boolean {
    const menuElement = this.elementRef.nativeElement.querySelector('.actions-menu');
    const buttonElement = this.ellipsisButton?.nativeElement;
    const mouseX = event.clientX;
    const mouseY = event.clientY;

    // Check if mouse is over button
    if (buttonElement) {
      const buttonRect = buttonElement.getBoundingClientRect();
      if (
        mouseX >= buttonRect.left &&
        mouseX <= buttonRect.right &&
        mouseY >= buttonRect.top &&
        mouseY <= buttonRect.bottom
      ) {
        return true;
      }
    }

    // Check if mouse is over menu
    if (menuElement) {
      const menuRect = menuElement.getBoundingClientRect();
      if (
        mouseX >= menuRect.left &&
        mouseX <= menuRect.right &&
        mouseY >= menuRect.top &&
        mouseY <= menuRect.bottom
      ) {
        return true;
      }
    }

    // Check if mouse is in the gap (16px from button right edge to menu left edge)
    if (buttonElement && menuElement) {
      const buttonRect = buttonElement.getBoundingClientRect();
      const menuRect = menuElement.getBoundingClientRect();

      // Check if mouse is in horizontal gap area
      if (
        mouseX >= buttonRect.right &&
        mouseX <= menuRect.left &&
        mouseY >= Math.min(buttonRect.top, menuRect.top) &&
        mouseY <= Math.max(buttonRect.bottom, menuRect.bottom)
      ) {
        return true;
      }
    }

    return false;
  }
}
